import React from "react";
import { useForm } from "react-hook-form";

import { getModuleDisplayName } from "src/adminPanel/modules/ModulesSettings";
import { useUpdateModule, ModuleSpec } from "src/api/workspacesV2Api";
import { Button } from "src/base-components/Button";
import { Input } from "src/base-components/Input";
import { WorkspaceDb } from "src/clients/flow-api";
import { OrganizationInDB } from "src/clients/taktile-api";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { logger } from "src/utils/logger";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  workspace: WorkspaceDb;
  organization: OrganizationInDB;
  moduleType: string;
  module: ModuleSpec;
  afterLeave?: () => void;
};

type FormData = {
  config?: Record<string, string>;
};

const getConfigFields = (module: ModuleSpec) => {
  const config = module.config || {};
  return Object.keys(config).map((key) => ({
    key,
    currentValue: config[key as keyof typeof config] ?? "",
  }));
};

const getStatusClasses = (status: string) => {
  if (status.toLowerCase().includes("complete")) {
    return "text-green-700 bg-green-100";
  }
  if (status.toLowerCase().includes("progress")) {
    return "text-yellow-700 bg-yellow-100";
  }
  if (status.toLowerCase().includes("failed")) {
    return "text-red-700 bg-red-100";
  }
  return "text-gray-700 bg-gray-100";
};

const getButtonText = (
  isSubmitting: boolean,
  hasChanges: boolean,
  changeCount: number,
) => {
  if (isSubmitting) {
    return "Saving...";
  }
  if (hasChanges) {
    return `Save ${changeCount} Change${changeCount !== 1 ? "s" : ""}`;
  }
  return "No Changes";
};

export const ModuleConfigModal: React.FC<Props> = ({
  isOpen,
  onClose,
  workspace,
  organization,
  moduleType,
  module,
  afterLeave,
}) => {
  const updateModule = useUpdateModule();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, dirtyFields },
    reset,
    getValues,
  } = useForm<FormData>({
    values: { config: module.config as Record<string, string> },
  });

  const configFields = getConfigFields(module);

  // Track which fields have changed using dirtyFields
  const getChangedFields = () => {
    const changes: Array<{ key: string; oldValue: string; newValue: string }> =
      [];

    if (dirtyFields.config) {
      const currentValues = getValues("config") || {};

      Object.keys(dirtyFields.config).forEach((key) => {
        const configAsRecord = module.config as Record<string, string>;
        const originalValue = configAsRecord?.[key] || "";
        const newValue = currentValues[key] || "";

        if (newValue !== "") {
          changes.push({
            key,
            oldValue: originalValue,
            newValue,
          });
        }
      });
    }

    return changes;
  };

  const changedFields = getChangedFields();
  const hasChanges = changedFields.length > 0;

  const onSubmit = async (data: FormData) => {
    try {
      // Only include config if there are changes
      if (!data.config || Object.keys(data.config).length === 0) {
        toastActions.default({
          title: "No changes detected",
          description: "No configuration changes were made.",
        });
        onClose();
        return;
      }

      const filteredConfig = Object.fromEntries(
        Object.entries(data.config).filter(([_, value]) => value !== ""),
      );

      if (Object.keys(filteredConfig).length === 0) {
        toastActions.default({
          title: "No changes detected",
          description: "No configuration changes were made.",
        });
        onClose();
        return;
      }

      await updateModule.mutateAsync({
        workspaceId: workspace.id,
        moduleType,
        data: { config: filteredConfig },
      });

      toastActions.success({
        title: "Configuration updated",
        description: `${getModuleDisplayName(moduleType)} configuration has been updated successfully.`,
      });

      onClose();
    } catch (error) {
      logger.error(`Error when updating module: ${error}`);
      toastActions.failure({
        title: "Update failed",
        description: `Failed to update ${getModuleDisplayName(moduleType)} configuration, ${error}`,
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      afterLeave={afterLeave}
      open={isOpen}
      size="lg"
      onClose={handleClose}
    >
      <Modal.Header>
        Configure {getModuleDisplayName(moduleType)}
        <p className="mt-2 text-gray-600 font-inter-normal-13px">
          Update configuration parameters for <strong>{workspace.name}</strong>
        </p>
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="space-y-6">
            {/* Module Info Card */}
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-4 text-gray-900 font-inter-semibold-13px">
                Module Details
              </h3>

              <div className="grid grid-cols-2 gap-4 font-inter-normal-13px">
                {/* Left Column */}
                <div className="space-y-3">
                  <div>
                    <div className="tracking-wide uppercase text-gray-500 font-inter-medium-12px">
                      Organization
                    </div>
                    <a
                      className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                      href={`/admin-panel/organizations?search=${organization.id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {organization.name}
                    </a>
                    <div className="text-gray-500 font-code-12">
                      ID: {organization.id}
                    </div>
                  </div>

                  <div>
                    <div className="tracking-wide uppercase text-gray-500 font-inter-medium-12px">
                      Workspace
                    </div>
                    <a
                      className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                      href={`/admin-panel/organizations?search=${workspace.id}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {workspace.name}
                    </a>
                    <div className="text-gray-500 font-code-12">
                      Slug: {workspace.slug}
                    </div>
                    <div className="text-gray-500 font-code-12">
                      ID: {workspace.id}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-3">
                  <div>
                    <div className="tracking-wide uppercase text-gray-500 font-inter-medium-12px">
                      Version
                    </div>
                    <code className="rounded border bg-white px-2 py-1 font-code-12">
                      {module.version}
                    </code>
                  </div>

                  <div>
                    <div className="tracking-wide uppercase text-gray-500 font-inter-medium-12px">
                      Status
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-inter-medium-12px ${getStatusClasses(module.status)}`}
                    >
                      {module.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration Fields */}
            {configFields.length > 0 ? (
              <div className="space-y-5">
                <div className="border-b border-gray-200 pb-2">
                  <h3 className="text-gray-900 font-inter-semibold-16px">
                    Configuration Parameters
                  </h3>
                  <p className="mt-1 text-gray-500 font-inter-normal-13px">
                    Modify the configuration values for this module
                  </p>
                </div>

                {configFields.map((field) => {
                  const currentValues = getValues("config") || {};
                  const currentValue = currentValues[field.key] || "";
                  const isChanged =
                    dirtyFields.config?.[field.key] && currentValue !== "";

                  return (
                    <div
                      key={field.key}
                      className={`rounded-lg border bg-white p-4 ${
                        isChanged ? "border-orange-300 bg-orange-50" : ""
                      }`}
                    >
                      <label
                        className="mb-3 block text-gray-900 font-inter-medium-13px"
                        htmlFor={`config.${field.key}`}
                      >
                        <code
                          className={`rounded-md border px-2.5 py-1 font-code-13 ${
                            isChanged
                              ? "text-orange-800 border-orange-300 bg-orange-100"
                              : "bg-blue-50 text-blue-800 border-blue-200"
                          }`}
                        >
                          {field.key}
                        </code>
                        {isChanged && (
                          <span className="text-orange-800 ml-2 inline-flex items-center rounded bg-orange-100 px-2 py-0.5 font-inter-medium-12px">
                            Changed
                          </span>
                        )}
                      </label>
                      <Input
                        {...register(
                          `config.${field.key}` as `config.${string}`,
                        )}
                        placeholder={`Enter value for ${field.key}`}
                        fullWidth
                      />
                      {field.currentValue && (
                        <div className="mt-2 text-gray-600 font-inter-normal-13px">
                          <span className="text-gray-500">Current: </span>
                          <code className="rounded border bg-gray-100 px-2 py-1 font-code-12">
                            {field.currentValue}
                          </code>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                <p>No configuration parameters found for this module.</p>
              </div>
            )}

            {/* Changes Summary */}
            {hasChanges && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                <h3 className="text-orange-900 mb-3 font-inter-semibold-13px">
                  📝 Summary of Changes ({changedFields.length})
                </h3>
                <div className="space-y-2">
                  {changedFields.map((change) => (
                    <div key={change.key} className="font-inter-normal-13px">
                      <code className="text-orange-800 rounded bg-orange-100 px-2 py-1 font-code-12">
                        {change.key}
                      </code>
                      <div className="ml-1 mt-1 text-orange-700 font-inter-normal-12px">
                        <span className="text-gray-500 line-through">
                          {change.oldValue || "(empty)"}
                        </span>
                        <span className="mx-2">→</span>
                        <span className="font-medium">{change.newValue}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal.Content>

        <Modal.Footer
          primaryButton={
            <Button
              disabled={
                isSubmitting || configFields.length === 0 || !hasChanges
              }
              htmlType="submit"
              loading={isSubmitting}
              variant="primary"
            >
              {getButtonText(isSubmitting, hasChanges, changedFields.length)}
            </Button>
          }
          secondaryButton="Cancel"
        />
      </form>
    </Modal>
  );
};
